import type { Lang } from "@/lib/i18n";
import type { SubPageContent } from "@/components/common/SubPageLayout";

export interface CylindricalScreensDiagramCopy {
  sectionTitle: string;
  sectionLead: string;
  profileTitle: string;
  profileDesc: string;
  axialTitle: string;
  axialDesc: string;
  circTitle: string;
  circDesc: string;
}

export interface CylindricalScreensPageData {
  content: SubPageContent;
  diagramCopy: CylindricalScreensDiagramCopy;
}

export const COPY: Record<Lang, CylindricalScreensPageData> = {
  en: {
    diagramCopy: {
      sectionTitle: "Cylindrical Screen Engineering & Slot Orientations",
      sectionLead: "Continuous resistance welding joins profile V-wires to support members, creating rigid structural cylinders tailored for mechanical scraping or high-volume rotary screening.",
      profileTitle: "Continuous Slot Geometry",
      profileDesc: "Inward-widening V-wire apertures prevent near-size particle entrapment and minimize operational differential pressure across the screen wall.",
      axialTitle: "Axial Slot Orientation",
      axialDesc: "Longitudinal surface wires provide unobstructed linear guidance for internal or external scraper blades and backwash arms.",
      circTitle: "Circumferential Slot Orientation",
      circDesc: "Continuously coiled helical profile wires wound on axial rods deliver exceptional hoop strength for rotary trommels and internal-feed drums.",
    },
    content: {
      breadcrumbKey: "CylindricalScreens",
      relatedTopic: "wedge",
      hero: {
        categoryBadge: "Wedge Wire Screens",
        standardBadge: "Continuous Slot V-Wire",
        heading: "Cylindrical Wedge Wire Screens",
        lead: "Precision-engineered stainless steel cylindrical screens featuring continuous resistance-welded V-wire profiles. Designed for industrial filtration, rotary drum screening, and mechanical scraper systems requiring non-clogging open area and structural collapse resistance.",
        secondaryCta: {
          label: "View Wedge Wire Screens",
          to: "/WedgeWireScreenPage",
        },
        image: {
          src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-product-photograph-of-sever-9a20f1.png",
          alt: "Industrial cylindrical stainless steel wedge wire filter screens and support cores",
        },
        cardNote: "Axial & Circumferential Slots",
        cardLink: {
          label: "Request a Quote",
          to: "/RequestAQuotePage",
        },
        specs: [
          { label: "Slot Width", value: "0.02 – 5.0 mm" },
          { label: "Diameter Range", value: "10 – 1200 mm" },
          { label: "Construction", value: "Resistance Welded V-Wire" },
          { label: "Standard Alloys", value: "AISI 304 / 316L" },
        ],
      },
      overview: {
        eyebrow: "Structural Rigidity & Aperture Control",
        heading: "Engineered Cylindrical Architecture for Demanding Filtration",
        paragraphs: [
          "MutualFilter cylindrical wedge wire screens are manufactured by fusion-welding cold-rolled V-shaped profile wires onto internal or external structural support rods at every intersection. This continuous resistance welding technique forms a rigid one-piece tubular geometry that withstands significant differential pressures, external radial loads, and continuous mechanical scraping cycles without slot deformation.",
          "Unlike conventional perforated or woven cylindrical media, the inverted triangular profile of the wedge wire creates an aperture that widens inward toward the downstream side. Particles smaller than the nominal slot opening pass through freely without jamming, while oversize solids accumulate on the continuous planar surface for easy removal by automated scrapers, brushes, or periodic backflushing.",
          "Cylindrical screens are configured in two distinct slot directions: axial slot cylinders with wires running parallel to the centerline for longitudinal scraping, and circumferential slot cylinders with continuous spiral wire windings around axial rods for rotary trommel and dewatering drums.",
        ],
        keyPoints: [
          "Continuous non-clogging V-wire geometry maintains high open area with low operational differential pressure.",
          "Resistance-welded profile wire and support rods prevent collapse under high surge pressure and mechanical scraping.",
          "All-stainless construction handles ultrasonic cleaning, backflushing, and chemical washing for long service life.",
        ],
        image: {
          src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-product-photograph-of-sever-9a20f1.png",
          alt: "Precision welded cylindrical wedge wire screen assembly with support elements",
        },
      },
      specTable: {
        heading: "Standard Cylindrical Screen Parameters",
        intro: "Custom fabricated to customer drawings with integrated flanges, end rings, and thread connectors. Parameters reflect standard manufacturing capabilities.",
        columns: [
          { key: "parameter", label: "Parameter", align: "left" },
          { key: "standardRange", label: "Standard Range", align: "left" },
          { key: "customRange", label: "Custom Options", align: "left" },
          { key: "notes", label: "Design Notes", align: "left" },
        ],
        rows: [
          {
            parameter: "Slot Width",
            standardRange: "0.02 – 5.0 mm",
            customRange: "Custom increments",
            notes: "Uniform continuous slot spacing across entire cylindrical length",
          },
          {
            parameter: "Outer Diameter",
            standardRange: "10 – 1200 mm",
            customRange: "Specific tooling diameters",
            notes: "Small bore strainers up to large-diameter rotary trommel drums",
          },
          {
            parameter: "Flow Direction",
            standardRange: "FOTI / FITO",
            customRange: "Reversed profile mounting",
            notes: "Flow-Out-To-In (external screen surface) or Flow-In-To-Out (internal smooth surface)",
          },
          {
            parameter: "Standard Alloys",
            standardRange: "AISI 304, AISI 316, 316L",
            customRange: "Alloy verification on request",
            notes: "Selected for corrosion resistance and process compatibility",
          },
          {
            parameter: "End Fittings",
            standardRange: "Plain edges, weld rings",
            customRange: "Flanges, threads, bail handles",
            notes: "Custom machined or welded fittings to integrate with existing housings",
          },
        ],
        caption: "Specifications based on standard resistance-welded wedge wire manufacturing limits.",
      },
      features: {
        heading: "Key Engineering Capabilities",
        intro: "Every cylindrical screen is built for reliable solid-liquid separation and extended service in continuous process environments.",
        items: [
          {
            title: "Precision V-Wire Geometry",
            text: "Smooth profile edges minimize flow turbulence and eliminate particle wedging at the slot opening.",
          },
          {
            title: "Rigid Structural Reinforcement",
            text: "Heavy-duty internal or external support rods provide superior collapse strength under differential pressure.",
          },
          {
            title: "Reversible Flow Configurations",
            text: "Available in both FOTI and FITO configurations with smooth internal or external working surfaces.",
          },
          {
            title: "Sanitary & Regenerable",
            text: "Crevice-free welded construction supports CIP washdown, backwash regeneration, and chemical sanitization.",
          },
        ],
      },
      related: {
        heading: "Explore Slot Orientation Variants",
        intro: "Select the slot configuration suited to your machine architecture and solids discharge method.",
        cards: [
          {
            title: "Axial Slot Cylinders",
            text: "Continuous slots run parallel to the cylinder axis, providing clean linear tracks for mechanical scraper systems, brush cleaning, and radial flow filtration.",
            to: "/AxialSlotCylindersPage",
            badge: "Parallel to Axis",
            image: {
              src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-product-photograph-of-an-ax-45d1d0.webp",
              alt: "Axial slot wedge wire cylindrical screen with longitudinal profile wire arrangement",
            },
            linkLabel: "View Axial Slot Cylinders",
          },
          {
            title: "Circumferential Slot Cylinders",
            text: "Continuous spiral wedge wire wound around the circumference on axial support rods, delivering maximum hoop strength for rotary trommels and internal-feed screening.",
            to: "/CircumferentialSlotCylindersPage",
            badge: "Around Circumference",
            image: {
              src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-product-photograph-of-a-cir-c1a7f2.webp",
              alt: "Stainless steel circumferential slot cylinder screen with continuous radial wedge wire for rotary trommels",
            },
            linkLabel: "View Circumferential Cylinders",
          },
        ],
      },
      cta: {
        id: "cylindrical-screens-quote",
        eyebrow: "Custom Cylindrical Mesh Solutions",
        heading: "Request a Custom Cylindrical Screen Quotation",
        lead: "Send your dimensional parameters, slot width requirements, and operating conditions to our engineering team for a comprehensive technical proposal.",
        secondaryLabel: "Browse All Wedge Wire Screens",
        secondaryTo: "/WedgeWireScreenPage",
        note: "Material test reports and positive material identification available when specified in the order.",
      },
    },
  },
  zh: {
    diagramCopy: {
      sectionTitle: "圆柱筛筒结构原理与缝隙走向",
      sectionLead: "全电阻熔焊工艺将 V 型截面轮廓丝与内部支撑构件精准连接，形成专为刮刀刮渣或大流量转鼓筛分量身定制的高强度刚性圆柱结构。",
      profileTitle: "连续防堵缝隙几何结构",
      profileDesc: "向内扩散的 V 型轮廓丝通道防止接近缝隙尺寸的固体颗粒卡塞，显著降低流经筛筒管壁的运行压差。",
      axialTitle: "轴向缝隙排列走向",
      axialDesc: "沿圆筒轴线纵向平行延伸的筛条，为内部或外部自清洗刮刀及反冲洗导流臂提供平整通畅的直线轨道。",
      circTitle: "环向缝隙排列走向",
      circDesc: "沿圆周 360 度连续螺旋转向缠绕的异型丝熔焊在轴向支撑筋上，赋予旋转转鼓与内进料筛筒优异的环向抗压溃强度。",
    },
    content: {
      breadcrumbKey: "CylindricalScreens",
      relatedTopic: "wedge",
      hero: {
        categoryBadge: "楔形丝筛网",
        standardBadge: "连续缝隙 V 型丝",
        heading: "圆柱形楔形丝筛网与滤筒",
        lead: "采用全电阻熔焊精密工艺制造的高强度不锈钢圆柱筛筒。专为工业流体过滤、转鼓筛分和机械刮刀自清洗系统设计，具备高开孔率、连续防堵缝隙以及出色的抗压溃性能。",
        secondaryCta: {
          label: "查看楔形丝产品总览",
          to: "/WedgeWireScreenPage",
        },
        image: {
          src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-product-photograph-of-sever-9a20f1.png",
          alt: "工业级高精度不锈钢圆柱楔形丝滤筒与内部支撑骨架",
        },
        cardNote: "轴向与环向缝隙可选",
        cardLink: {
          label: "获取工程报价",
          to: "/RequestAQuotePage",
        },
        specs: [
          { label: "缝隙宽度", value: "0.02 – 5.0 mm" },
          { label: "直径范围", value: "10 – 1200 mm" },
          { label: "结构形式", value: "全电阻熔焊 V 型丝" },
          { label: "标准材质", value: "AISI 304 / 316L" },
        ],
      },
      overview: {
        eyebrow: "结构刚性与精密缝隙控制",
        heading: "专为苛刻过滤工况打造的高强度圆柱结构",
        paragraphs: [
          "MutualFilter 圆柱形楔形丝筛筒通过在每个交叉节点处将冷轧 V 型轮廓丝全熔焊在内部或外部支撑构件上制造而成。这种全电阻熔焊工艺构建了坚固的一体化筒状结构，即使在剧烈压差脉冲、外部径向高负荷以及高频机械刮渣冲击下，也能保持微米缝隙尺寸恒定不变。",
          "与传统冲孔网或编织网滤筒不同，楔形丝截面独特的倒三角形几何形态形成了向介质流向下游方向逐渐扩大的通道。小于设定缝隙公称尺寸的固体微粒能迅速穿透而不产生嵌卡，大于缝隙的截留杂质则聚集在连续平滑的拦截面上，易于被刮刀、毛刷或周期性反冲洗迅速剥离清除。",
          "圆柱筛筒提供两种核心缝隙构造：轴向缝隙滤筒（型材丝平行于圆筒轴线，适合直线刮渣）与环向缝隙筛筒（型材丝沿圆周方向连续螺旋缠绕，适合旋转滚筒筛与脱水离心分离）。",
        ],
        keyPoints: [
          "连续自清洁 V 型截面几何结构，保持高开孔率并显著降低运行初始压差。",
          "全电阻熔焊支撑筋条与轮廓丝紧密结合，有效抵御瞬态高压冲击与机械刮渣磨损。",
          "全不锈钢结构支持超声波清洗、反冲洗及酸碱化学浸洗，具备长期循环使用寿命。",
        ],
        image: {
          src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-product-photograph-of-sever-9a20f1.png",
          alt: "高精度全焊接圆柱楔形丝筛筒总成",
        },
      },
      specTable: {
        heading: "圆柱形楔形丝筛筒标准参数表",
        intro: "可根据客户图纸精准定制安装法兰、端环、螺纹接口及固定构件。以下数据代表标准制造能力范围。",
        columns: [
          { key: "parameter", label: "技术参数", align: "left" },
          { key: "standardRange", label: "标准范围", align: "left" },
          { key: "customRange", label: "定制选项", align: "left" },
          { key: "notes", label: "工程说明", align: "left" },
        ],
        rows: [
          {
            parameter: "缝隙宽度",
            standardRange: "0.02 – 5.0 mm",
            customRange: "按需设定公差",
            notes: "全圆周及轴向全长范围保持高度均匀一致的缝隙公差",
          },
          {
            parameter: "筒体外径",
            standardRange: "10 – 1200 mm",
            customRange: "特殊模具规格",
            notes: "从高压精密管道过滤器到超大口径旋转转鼓筛网",
          },
          {
            parameter: "介质流向",
            standardRange: "FOTI / FITO",
            customRange: "内外工作面反转",
            notes: "由外向内（外表面截留过滤）或由内向外（内表面平滑无阻流）",
          },
          {
            parameter: "标准材质",
            standardRange: "AISI 304, AISI 316, 316L",
            customRange: "材质检测分析报告",
            notes: "根据工艺耐腐蚀要求与化学接触介质合理选配",
          },
          {
            parameter: "端部连接",
            standardRange: "平头切口、焊接环",
            customRange: "标准法兰、外螺纹、吊环",
            notes: "支持精密机加工或焊接接头，无缝装配至现有设备壳体",
          },
        ],
        caption: "数据基于标准全电阻熔焊楔形丝工艺公差范围。",
      },
      features: {
        heading: "核心工程制造优势",
        intro: "每一个圆柱筛筒均根据工业连续工况的高可靠性与长周期运行要求打造。",
        items: [
          {
            title: "精密 V 型丝截面",
            text: "平滑的截面棱边有效抑制介质流体扰动，从根源上杜绝颗粒夹塞卡滞。",
          },
          {
            title: "高抗压溃刚性骨架",
            text: "全熔焊重型加强筋骨架，在高瞬时反冲压差或刮刀机械推力下稳定不变形。",
          },
          {
            title: "双向流向适配设计",
            text: "支持 FOTI 与 FITO 自由选配，提供光洁平整的内工作面或外工作面。",
          },
          {
            title: "全金属易再生结构",
            text: "全不锈钢卫生级焊接无盲区死角，完美耐受高温蒸汽、在线 CIP 与化学浸洗。",
          },
        ],
      },
      related: {
        heading: "探索不同缝隙排列方向的子产品",
        intro: "根据机械清渣刮洗方式和介质流向特征，选择相匹配的缝隙走向方案。",
        cards: [
          {
            title: "轴向缝隙滤筒",
            text: "连续缝隙沿圆柱轴向平行延伸，为自清洗刮刀、内/外部机械刮渣与径向流体分离提供平滑的线性导向表面。",
            to: "/AxialSlotCylindersPage",
            badge: "轴向平行排列",
            image: {
              src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-product-photograph-of-an-ax-45d1d0.webp",
              alt: "轴向缝隙楔形丝圆柱筛筒与平行排列结构",
            },
            linkLabel: "查看轴向滤筒详情",
          },
          {
            title: "环向缝隙圆筒筛",
            text: "楔形丝沿圆筒圆周方向连续缠绕焊接，配置轴向支撑筋条，专为旋转转鼓筛、滚筒筛及内进料物料轴向推进分选而设计。",
            to: "/CircumferentialSlotCylindersPage",
            badge: "圆周环绕排列",
            image: {
              src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-product-photograph-of-a-cir-c1a7f2.webp",
              alt: "全周向环绕缝隙不锈钢圆筒筛网与转鼓分选结构",
            },
            linkLabel: "查看环向圆筒筛详情",
          },
        ],
      },
      cta: {
        id: "cylindrical-screens-quote",
        eyebrow: "圆柱形楔形丝滤筒定制方案",
        heading: "获取圆柱筛筒技术参数与报价方案",
        lead: "请提供您的筒体尺寸、过滤缝隙宽度及工况运行参数，我们的工程技术团队将为您出具详细的选型方案与制造图纸。",
        secondaryLabel: "返回楔形丝产品总览",
        secondaryTo: "/WedgeWireScreenPage",
        note: "订单明确要求时可按需提供材质检验报告与成分检测光谱分析。",
      },
    },
  },
  es: {
    diagramCopy: {
      sectionTitle: "Ingeniería de Cilindros Filtrantes y Orientaciones de Ranura",
      sectionLead: "La soldadura por resistencia continua une los alambres en V a las varillas de soporte, creando cilindros estructurales rígidos adaptados a sistemas de raspado o cribado rotativo.",
      profileTitle: "Geometría Continua Anticolmatación",
      profileDesc: "La abertura ensanchada hacia el interior evita el atrapamiento de partículas críticas y reduce la caída de presión a través del cilindro.",
      axialTitle: "Orientación de Ranura Axial",
      axialDesc: "Alambres de perfil longitudinales que ofrecen guías lineales sin obstáculos para cuchillas raspadoras y brazos de retrolavado.",
      circTitle: "Orientación de Ranura Circunferencial",
      circDesc: "Alambres enrollados de forma continua sobre varillas axiales que proporcionan una alta resistencia al colapso radial en tromeles y tambores rotativos.",
    },
    content: {
      breadcrumbKey: "CylindricalScreens",
      relatedTopic: "wedge",
      hero: {
        categoryBadge: "Alambre en Cuña",
        standardBadge: "Ranura Continua en V",
        heading: "Cilindros Filtrantes de Alambre en Cuña",
        lead: "Cilindros reforzados de acero inoxidable con soldadura continua en hilo en V. Diseñados para filtración industrial, cribado en tambores rotativos y sistemas de raspado mecánico que requieren alta superficie abierta anticolmatación y resistencia al colapso.",
        secondaryCta: {
          label: "Ver Alambre en Cuña",
          to: "/WedgeWireScreenPage",
        },
        image: {
          src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-product-photograph-of-sever-9a20f1.png",
          alt: "Telas filtrantes cilíndricas industriales de alambre en cuña de acero inoxidable",
        },
        cardNote: "Ranura Axial y Circunferencial",
        cardLink: {
          label: "Solicitar Presupuesto",
          to: "/RequestAQuotePage",
        },
        specs: [
          { label: "Ancho de ranura", value: "0.02 – 5.0 mm" },
          { label: "Rango de diámetro", value: "10 – 1200 mm" },
          { label: "Construcción", value: "Soldadura por resistencia en hilo en V" },
          { label: "Aleaciones estándar", value: "AISI 304 / 316L" },
        ],
      },
      overview: {
        eyebrow: "Rigidez Estructural y Precisión de Ranura",
        heading: "Arquitectura Cilíndrica Diseñada para Filtración Exigente",
        paragraphs: [
          "Los cilindros filtrantes de alambre en cuña MutualFilter se fabrican soldando por fusión alambres laminados en V sobre varillas de soporte estructurales en cada punto de cruce. Esta técnica forma un cilindro rígido de una sola pieza que resiste elevadas presiones diferenciales y raspado mecánico sin deformación de ranura.",
          "A diferencia de las mallas convencionales o chapas perforadas, el perfil triangular invertido genera una ranura que se ensancha hacia el interior en el sentido del flujo. Las partículas pasan sin atascarse, facilitando la limpieza continua por raspado o retrolavado periódico.",
          "Se ofrecen dos orientaciones de ranura: cilindros axiales con alambres paralelos al eje longitudinal para raspado mecánico, y cilindros circunferenciales con alambres en espiral continua para tambores rotativos y tromeles de deshidratación.",
        ],
        keyPoints: [
          "Geometría continua anticolmatación de hilo en V que mantiene una elevada superficie abierta con baja pérdida de carga.",
          "Estructura soldada por resistencia de hilo perfilado y varillas de soporte que previene el colapso ante sobrepresiones.",
          "Construcción íntegra en acero inoxidable apta para lavado por ultrasonidos, retrolavado y limpieza química.",
        ],
        image: {
          src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-product-photograph-of-sever-9a20f1.png",
          alt: "Conjunto cilíndrico de alambre en cuña soldado de precisión",
        },
      },
      specTable: {
        heading: "Parámetros Estándar de Cilindros Filtrantes",
        intro: "Fabricación a medida según planos con bridas, aros de refuerzo y conexiones roscadas integradas.",
        columns: [
          { key: "parameter", label: "Parámetro", align: "left" },
          { key: "standardRange", label: "Rango Estándar", align: "left" },
          { key: "customRange", label: "Opciones a Medida", align: "left" },
          { key: "notes", label: "Notas de Diseño", align: "left" },
        ],
        rows: [
          {
            parameter: "Ancho de ranura",
            standardRange: "0.02 – 5.0 mm",
            customRange: "Tolerancias específicas",
            notes: "Separación uniforme en toda la longitud del cilindro",
          },
          {
            parameter: "Diámetro exterior",
            standardRange: "10 – 1200 mm",
            customRange: "Herramentales especiales",
            notes: "Desde filtros tubulares pequeños hasta tromeles rotativos de gran diámetro",
          },
          {
            parameter: "Dirección de flujo",
            standardRange: "FOTI / FITO",
            customRange: "Superficie de trabajo invertida",
            notes: "Flujo de afuera hacia adentro o de adentro hacia afuera",
          },
          {
            parameter: "Aleaciones estándar",
            standardRange: "AISI 304, AISI 316, 316L",
            customRange: "Certificados de aleación bajo pedido",
            notes: "Seleccionadas para resistencia a la corrosión y compatibilidad química",
          },
          {
            parameter: "Terminaciones",
            standardRange: "Bordes lisos, anillos soldados",
            customRange: "Bridas mecanizadas, roscas",
            notes: "Integración precisa en carcasas y recipientes a presión existentes",
          },
        ],
        caption: "Valores basados en límites estándar de fabricación de alambre en cuña soldado por resistencia.",
      },
      features: {
        heading: "Capacidades Técnicas Clave",
        intro: "Cada cilindro está construido para una separación sólido-líquido fiable y un servicio continuo prolongado.",
        items: [
          {
            title: "Geometría Precisa en V",
            text: "Aristas perfiladas lisas que evitan el atascamiento de sólidos en la abertura de entrada.",
          },
          {
            title: "Refuerzo Estructural Rígido",
            text: "Varillas de soporte robustas que previenen el colapso ante variaciones de presión diferencial.",
          },
          {
            title: "Configuraciones Reversibles",
            text: "Disponibilidad en configuraciones FOTI y FITO con superficie de trabajo perfectamente lisa.",
          },
          {
            title: "Higiene y Regenerabilidad",
            text: "Construcción soldada sin hendiduras apta para retrolavado y desinfección química repetida.",
          },
        ],
      },
      related: {
        heading: "Explore las Variantes de Ranura",
        intro: "Seleccione la orientación óptima según el mecanismo de limpieza y la configuración de su equipo.",
        cards: [
          {
            title: "Cilindros de Ranura Axial",
            text: "Las ranuras continuas corren paralelas al eje del cilindro, ofreciendo pistas lineales limpias para raspadores mecánicos y filtración radial de alto caudal.",
            to: "/AxialSlotCylindersPage",
            badge: "Paralela al eje",
            image: {
              src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-product-photograph-of-an-ax-45d1d0.webp",
              alt: "Cilindro de alambre en cuña con ranura axial y perfiles longitudinales",
            },
            linkLabel: "Ver Cilindros Axiales",
          },
          {
            title: "Cilindros de Ranura Circunferencial",
            text: "Los alambres se enrollan de forma continua alrededor de la circunferencia con varillas axiales de soporte, diseñados para tromeles rotativos y tambores de alimentación interna.",
            to: "/CircumferentialSlotCylindersPage",
            badge: "Circunferencial",
            image: {
              src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-product-photograph-of-a-cir-c1a7f2.webp",
              alt: "Cilindro de ranura circunferencial de alambre en cuña para tamizado rotativo",
            },
            linkLabel: "Ver Cilindros Circunferenciales",
          },
        ],
      },
      cta: {
        id: "cylindrical-screens-quote",
        eyebrow: "Soluciones Cilíndricas a Medida",
        heading: "Solicite Cotización para Cilindros Filtrantes",
        lead: "Envíe sus especificaciones dimensionales, ranura requerida y condiciones de trabajo a nuestro equipo técnico.",
        secondaryLabel: "Volver a Alambre en Cuña",
        secondaryTo: "/WedgeWireScreenPage",
        note: "Certificados de ensayo de materiales e identificación positiva de materiales disponibles bajo pedido.",
      },
    },
  },
  de: {
    diagramCopy: {
      sectionTitle: "Spaltsiebzylinder-Konstruktion & Schlitzausrichtungen",
      sectionLead: "Kontinuierliche Widerstandsschweißung verbindet V-förmige Profildrähte mit Tragstäben zu formstabilen Zylindern für Abstreifersysteme und Trommelfiltration.",
      profileTitle: "Kontinuierliche Spaltgeometrie",
      profileDesc: "Nach innen erweiterte V-Draht-Schlitze verhindern das Verklemmen von Grenzkörnern und minimieren den Druckverlust über die Zylinderwand.",
      axialTitle: "Axiale Schlitzausrichtung",
      axialDesc: "Längs verlaufende Profildrähte bilden unterbrechungsfreie Führungsbahnen für mechanische Abstreiferklingen und Rückspülarme.",
      circTitle: "Umlaufende Schlitzausrichtung",
      circDesc: "Kontinuierlich auf axiale Stäbe gewickelte Profildrähte bieten maximale radiale Druckfestigkeit für Rotationstrommeln und Eindickzylinder.",
    },
    content: {
      breadcrumbKey: "CylindricalScreens",
      relatedTopic: "wedge",
      hero: {
        categoryBadge: "Spaltsiebe",
        standardBadge: "Kontinuierlicher V-Draht-Spalt",
        heading: "Zylindrische Spaltsiebe & Filterzylinder",
        lead: "Präzisionsgefertigte zylindrische Edelstahl-Spaltsiebe mit widerstandsgeschweißten V-Profilen. Konstruiert für industrielle Filtration, Rotationstrommelsiebung und mechanische Abstreifersysteme mit verstopfungsfreier Spaltgeometrie und hoher Stabilität.",
        secondaryCta: {
          label: "Alle Spaltsiebe ansehen",
          to: "/WedgeWireScreenPage",
        },
        image: {
          src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-product-photograph-of-sever-9a20f1.png",
          alt: "Industrielle zylindrische Edelstahl-Spaltsiebe und Filterstützkörbe",
        },
        cardNote: "Axial- und Umfangsschlitze",
        cardLink: {
          label: "Angebot anfordern",
          to: "/RequestAQuotePage",
        },
        specs: [
          { label: "Spaltweite", value: "0.02 – 5.0 mm" },
          { label: "Durchmesserbereich", value: "10 – 1200 mm" },
          { label: "Konstruktion", value: "Widerstandsgeschweißter V-Draht" },
          { label: "Standard-Legierungen", value: "AISI 304 / 316L" },
        ],
      },
      overview: {
        eyebrow: "Strukturelle Festigkeit & Spaltgenauigkeit",
        heading: "Präzise Zylinderarchitektur für Anspruchsvolle Filtrationsprozesse",
        paragraphs: [
          "MutualFilter Spaltsiebzylinder werden durch Widerstandsverschweißung kaltgewalzter V-Drähte mit internen oder externen Tragstäben an jedem Kreuzungspunkt gefertigt. Dieser Schweißprozess erzeugt ein formstabiles Rohr, das hohen Differenzdrücken und mechanischen Abstreifkräften ohne Spaltaufweitung standhält.",
          "Im Unterschied zu Lochblechen oder Drahtgeweben bildet das umgekehrte Dreiecksprofil des Spaltdrahts eine nach innen weiter werdende Öffnung. Feststoffe passieren ungehindert, während Überkorn auf der planen Arbeitsfläche verbleibt und leicht abgereinigt werden kann.",
          "Zwei Hauptausrichtungen stehen zur Verfügung: Axialschlitzzylinder mit achsenparallelen Drähten für lineare Abstreifung sowie Umfangsschlitzzylinder mit wendelförmiger Drahtwicklung für Trommelfilter und Siebzentrifugen.",
        ],
        keyPoints: [
          "Kontinuierliche verstopfungsfreie V-Draht-Geometrie sichert hohe freie Siebfläche bei minimalem Betriebsdruckverlust.",
          "Widerstandsgeschweißte Profil- und Tragstäbe widerstehen hohen Druckstößen und mechanischen Abstreifkräften.",
          "Voll-Edelstahlbauweise ermöglicht Ultraschallreinigung, Rückspülung und chemische Regenerierung für lange Standzeiten.",
        ],
        image: {
          src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-product-photograph-of-sever-9a20f1.png",
          alt: "Präzisionsgeschweißte Spaltsiebzylinder-Baugruppe",
        },
      },
      specTable: {
        heading: "Standard-Parameter für Spaltsiebzylinder",
        intro: "Individuelle Fertigung nach Zeichnung mit Flanschen, Endringen und Gewindeanschlüssen.",
        columns: [
          { key: "parameter", label: "Parameter", align: "left" },
          { key: "standardRange", label: "Standardbereich", align: "left" },
          { key: "customRange", label: "Optionen", align: "left" },
          { key: "notes", label: "Konstruktionshinweis", align: "left" },
        ],
        rows: [
          {
            parameter: "Spaltweite",
            standardRange: "0.02 – 5.0 mm",
            customRange: "Fein abgestuft",
            notes: "Gleichmäßige kontinuierliche Spaltweite über die gesamte Zylinderlänge",
          },
          {
            parameter: "Außendurchmesser",
            standardRange: "10 – 1200 mm",
            customRange: "Sonderwerkzeuge",
            notes: "Von kompakten Schutzsiebrohren bis zu großen Rotationstrommeln",
          },
          {
            parameter: "Durchströmung",
            standardRange: "FOTI / FITO",
            customRange: "Umgekehrte Profile",
            notes: "Von außen nach innen oder von innen nach außen durchströmt",
          },
          {
            parameter: "Standard-Legierungen",
            standardRange: "AISI 304, AISI 316, 316L",
            customRange: "Zeugnisse auf Anfrage",
            notes: "Abgestimmt auf Korrosionsbelastung und Medienverträglichkeit",
          },
          {
            parameter: "Endanschlüsse",
            standardRange: "Glatte Enden, Schweißringe",
            customRange: "Flansche, Gewindenippel",
            notes: "Passgenaue Integration in vorhandene Filter- und Maschinengehäuse",
          },
        ],
        caption: "Werte auf Basis standardmäßiger Fertigungsgrenzen für widerstandsgeschweißte Spaltsiebe.",
      },
      features: {
        heading: "Wesentliche Konstruktionsvorteile",
        intro: "Jeder Zylinder ist für zuverlässige Fest-Flüssig-Trennung und lange Standzeiten in industriellen Dauerprozessen ausgelegt.",
        items: [
          {
            title: "Präzises V-Draht-Profil",
            text: "Glatte Kanten verhindern Verwirbelungen und vermeiden das Festsetzen von Partikeln im Schlitz.",
          },
          {
            title: "Hohe Druckfestigkeit",
            text: "Robuste Tragstäbe widerstehen starken Differenzdrücken und mechanischen Abstreifkräften.",
          },
          {
            title: "Flexible Strömungsrichtung",
            text: "Ausführung in FOTI und FITO mit absolut glatter Arbeitsfläche auf Wunsch innen oder außen.",
          },
          {
            title: "Hygienisch & Regenerierbar",
            text: "Totraumfreie Schweißkonstruktion ermöglicht CIP-Reinigung, Rückspülung und Desinfektion.",
          },
        ],
      },
      related: {
        heading: "Schlitzausrichtungen im Überblick",
        intro: "Wählen Sie die passende Schlitzausrichtung für Ihr Maschinenkonzept und Abreinigungsverfahren.",
        cards: [
          {
            title: "Axialschlitz-Zylinder",
            text: "Kontinuierliche Schlitze verlaufen achsenparallel und bilden saubere Führungsbahnen für mechanische Abstreifer, Rückspülfilter und Radialdurchströmung.",
            to: "/AxialSlotCylindersPage",
            badge: "Achsenparallel",
            image: {
              src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-product-photograph-of-an-ax-45d1d0.webp",
              alt: "Axialschlitz-Spaltsiebzylinder mit achsenparallelem Profildraht",
            },
            linkLabel: "Zu den Axialschlitz-Zylindern",
          },
          {
            title: "Umfangsschlitz-Zylinder",
            text: "Profildrähte verlaufen kontinuierlich um den Zylinderumfang auf axialen Tragstäben, optimal für Trommelsiebe, Rotationsfilter und axialen Materialtransport.",
            to: "/CircumferentialSlotCylindersPage",
            badge: "Umlaufend",
            image: {
              src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-product-photograph-of-a-cir-c1a7f2.webp",
              alt: "Spaltsiebzylinder mit Umfangsschlitz für Trommelfiltration und Drehrohre",
            },
            linkLabel: "Zu den Umfangsschlitz-Zylindern",
          },
        ],
      },
      cta: {
        id: "cylindrical-screens-quote",
        eyebrow: "Individuelle Zylinderlösungen",
        heading: "Angebot für Spaltsiebzylinder anfordern",
        lead: "Senden Sie uns Abmessungen, Spaltweiten und Einsatzbedingungen für eine fundierte technische Auslegung.",
        secondaryLabel: "Zurück zu Spaltsiebe",
        secondaryTo: "/WedgeWireScreenPage",
        note: "Materialprüfzeugnisse und Verwechslungsprüfungen auf Anforderung im Auftrag lieferbar.",
      },
    },
  },
  fr: {
    diagramCopy: {
      sectionTitle: "Ingénierie des Cylindres et Orientations des Fentes",
      sectionLead: "Le soudage continu par résistance assemble les fils profilés en V aux supports axiaux, formant un ensemble rigide idéal pour le raclage ou le tamisage rotatif.",
      profileTitle: "Géométrie Continue Anticolmatage",
      profileDesc: "L'élargissement intérieur des fentes prévient le piégeage des particules de taille limite et limite la perte de charge à travers la paroi du cylindre.",
      axialTitle: "Orientation des Fentes Axiales",
      axialDesc: "Les fils longitudinaux procurent une surface de guidage linéaire sans à-coup pour les racleurs mécaniques et les bras de rétro-lavage.",
      circTitle: "Orientation des Fentes Circonférentielles",
      circDesc: "L'enroulement hélicoïdal continu soudé sur des tiges axiales garantit une excellente résistance à l'écrasement pour les trommels rotatifs.",
    },
    content: {
      breadcrumbKey: "CylindricalScreens",
      relatedTopic: "wedge",
      hero: {
        categoryBadge: "Fils Profilés",
        standardBadge: "Fente Continue en V",
        heading: "Cylindres Filtrants en Fil Profilé",
        lead: "Cylindres en acier inoxydable de haute précision à fentes continues en fil profilé V soudé par résistance. Conçus pour la filtration industrielle, les tambours rotatifs et les systèmes à racleur mécanique nécessitant une surface ouverte autonettoyante et une forte résistance mécanique.",
        secondaryCta: {
          label: "Voir les Tamis en Fil Profilé",
          to: "/WedgeWireScreenPage",
        },
        image: {
          src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-product-photograph-of-sever-9a20f1.png",
          alt: "Tamis cylindriques industriels en fil profilé inox et éléments de renfort",
        },
        cardNote: "Fentes Axiales et Circonférentielles",
        cardLink: {
          label: "Demander un Devis",
          to: "/RequestAQuotePage",
        },
        specs: [
          { label: "Largeur de fente", value: "0.02 – 5.0 mm" },
          { label: "Plage de diamètres", value: "10 – 1200 mm" },
          { label: "Fabrication", value: "Soudure par résistance de fil profilé" },
          { label: "Alliages standards", value: "AISI 304 / 316L" },
        ],
      },
      overview: {
        eyebrow: "Rigidité Structurelle et Maîtrise des Fentes",
        heading: "Architecture Cylindrique Conçue pour Procédés Exigeants",
        paragraphs: [
          "Les cylindres filtrants en fil profilé MutualFilter sont produits par soudure continue de fils en V sur des armatures structurales. Cette structure monobloc indéformable résiste aux différentiels de pression importants, aux charges radiales et aux contraintes répétées de raclage mécanique.",
          "Contrairement aux toiles tissées ou tôles perforées, le profil conique en V crée une fente qui s'élargit vers l'aval. Les particules fines traversent librement sans colmater l'ouverture, tandis que les matières retenues en surface sont facilement éliminées par raclage ou lavage inverse.",
          "Deux orientations de fentes sont disponibles : les cylindres à fentes axiales avec fils longitudinaux pour raclage linéaire, et les cylindres à fentes circonférentielles à enroulement hélicoïdal pour trommels et tambours de séparation.",
        ],
        keyPoints: [
          "Géométrie continue autonettoyante en fil profilé assurant une surface ouverte maximale et une faible perte de charge.",
          "Assemblage soudé par résistance entre fils profilés et supports prévenant l'affaissement sous fortes surpressions.",
          "Fabrication intégrale en acier inoxydable compatible avec nettoyage ultrason, rétro-lavage et bains chimiques répétés.",
        ],
        image: {
          src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-product-photograph-of-sever-9a20f1.png",
          alt: "Ensemble tamisant cylindrique soudé de précision",
        },
      },
      specTable: {
        heading: "Paramètres Standards des Cylindres Filtrants",
        intro: "Fabrication sur mesure d'après plans avec brides, viroles d'extrémité et raccords filetés.",
        columns: [
          { key: "parameter", label: "Paramètre", align: "left" },
          { key: "standardRange", label: "Plage Standard", align: "left" },
          { key: "customRange", label: "Options sur Mesure", align: "left" },
          { key: "notes", label: "Remarques", align: "left" },
        ],
        rows: [
          {
            parameter: "Largeur de fente",
            standardRange: "0.02 – 5.0 mm",
            customRange: "Tolérances fines",
            notes: "Écartement constant sur toute la circonférence et la hauteur du cylindre",
          },
          {
            parameter: "Diamètre extérieur",
            standardRange: "10 – 1200 mm",
            customRange: "Outillages dédiés",
            notes: "Du panier tubulaire compact au grand tambour rotatif de déshydratation",
          },
          {
            parameter: "Sens du flux",
            standardRange: "FOTI / FITO",
            customRange: "Face lisse inversée",
            notes: "De l'extérieur vers l'intérieur ou de l'intérieur vers l'extérieur",
          },
          {
            parameter: "Alliages standards",
            standardRange: "AISI 304, AISI 316, 316L",
            customRange: "Analyses matière sur demande",
            notes: "Choisis selon l'agressivité chimique et les normes de process",
          },
          {
            parameter: "Embouts de raccordement",
            standardRange: "Bords bruts, bagues soudées",
            customRange: "Brides usinées, filetages",
            notes: "Adaptation directe aux corps de filtres et carters industriels",
          },
        ],
        caption: "Spécifications selon limites de fabrication standards du fil profilé soudé par résistance.",
      },
      features: {
        heading: "Atouts Techniques de Fabrication",
        intro: "Chaque cylindre est façonné pour garantir une séparation solide-liquide continue et fiable.",
        items: [
          {
            title: "Profil V de Précision",
            text: "Arêtes franches supprimant les turbulences et les risques d'encrassement des fentes.",
          },
          {
            title: "Armature Haute Résistance",
            text: "Supports soudés robustes assurant une tenue exemplaire sous forts différentiels de pression.",
          },
          {
            title: "Configurations de Flux Réversibles",
            text: "Versions FOTI et FITO avec face utile parfaitement plane à l'intérieur ou à l'extérieur.",
          },
          {
            title: "Nettoyabilité et Régénération",
            text: "Construction tout inox sans zone morte compatible avec les cycles NEP et bains acides.",
          },
        ],
      },
      related: {
        heading: "Variantes d'Orientation des Fentes",
        intro: "Choisissez la disposition géométrique convenant à votre cinématique de nettoyage.",
        cards: [
          {
            title: "Cylindres à Fentes Axiales",
            text: "Les fentes continues sont parallèles à l'axe du cylindre, offrant une surface linéaire lisse pour les racleurs mécaniques et la séparation radiale à fort débit.",
            to: "/AxialSlotCylindersPage",
            badge: "Parallèle à l'axe",
            image: {
              src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-product-photograph-of-an-ax-45d1d0.webp",
              alt: "Cylindre en fil profilé à fentes axiales et renforts longitudinaux",
            },
            linkLabel: "Voir les Cylindres Axiaux",
          },
          {
            title: "Cylindres à Fente Circonférentielle",
            text: "Les fils profilés s'enroulent autour de la circonférence sur des tiges axiales, conçus pour les trommels rotatifs, tambours et filtres à progression axiale.",
            to: "/CircumferentialSlotCylindersPage",
            badge: "Circonférentielle",
            image: {
              src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-product-photograph-of-a-cir-c1a7f2.webp",
              alt: "Cylindre en fil profilé à fente circonférentielle pour trommel rotatif",
            },
            linkLabel: "Voir les Cylindres Circonférentiels",
          },
        ],
      },
      cta: {
        id: "cylindrical-screens-quote",
        eyebrow: "Solutions Cylindriques Personnalisées",
        heading: "Obtenir une Étude et un Devis de Cylindre Filtrant",
        lead: "Transmettez-nous vos dimensions, fente utile et conditions d'exploitation pour une réponse technique complète.",
        secondaryLabel: "Retour aux Tamis en Fil Profilé",
        secondaryTo: "/WedgeWireScreenPage",
        note: "Certificats matière et analyses spectrométriques disponibles sur demande dans la commande.",
      },
    },
  },
  pt: {
    diagramCopy: {
      sectionTitle: "Engenharia de Cilindros Filtrantes e Orientações de Fenda",
      sectionLead: "Soldagem contínua por resistência une os arames em V às barras de sustentação, compondo cilindros estruturais rígidos para raspadores ou peneiramento rotativo.",
      profileTitle: "Geometria Contínua Antiobstrução",
      profileDesc: "Abertura que se alarga para o interior evita o aprisionamento de partículas e minimiza a perda de carga através da parede do cilindro.",
      axialTitle: "Orientação de Fenda Axial",
      axialDesc: "Arames longitudinais que formam trilhos lineares lisos para lâminas raspadoras e braços de retrolavagem.",
      circTitle: "Orientação de Fenda Circunferencial",
      circDesc: "Arames contínuos enrolados em espiral sobre barras axiais oferecem alta rigidez circunferencial para trommels e tambores rotativos.",
    },
    content: {
      breadcrumbKey: "CylindricalScreens",
      relatedTopic: "wedge",
      hero: {
        categoryBadge: "Arame em Cunha",
        standardBadge: "Fenda Contínua em V",
        heading: "Cilindros Filtrantes de Arame em Cunha",
        lead: "Cilindros reforçados de aço inoxidável com fendas contínuas de arame perfilado em V soldados por resistência. Projetados para filtração industrial, peneiramento em tambores rotativos e sistemas de raspadores mecânicos com alta área aberta antientupimento e resistência ao colapso.",
        secondaryCta: {
          label: "Ver Telas em Cunha",
          to: "/WedgeWireScreenPage",
        },
        image: {
          src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-product-photograph-of-sever-9a20f1.png",
          alt: "Telas filtrantes cilíndricas industriais de arame em cunha e tubos de suporte",
        },
        cardNote: "Fendas Axiais e Circunferenciais",
        cardLink: {
          label: "Solicitar Cotação",
          to: "/RequestAQuotePage",
        },
        specs: [
          { label: "Abertura da fenda", value: "0.02 – 5.0 mm" },
          { label: "Faixa de diâmetro", value: "10 – 1200 mm" },
          { label: "Construção", value: "Solda por resistência em arame em V" },
          { label: "Ligas padrão", value: "AISI 304 / 316L" },
        ],
      },
      overview: {
        eyebrow: "Rigidez Estrutural e Precisão de Abertura",
        heading: "Arquitetura Cilíndrica Projetada para Filtração Severa",
        paragraphs: [
          "Os cilindros filtrantes de arame em cunha MutualFilter são construídos soldando por fusão elétrica arames laminados em V sobre barras de sustentação em cada cruzamento. Essa técnica cria um tubo monobloco rígido que suporta elevadas pressões diferenciais e esforços de raspagem mecânica contínua.",
          "Diferentemente de telas convencionais ou chapas perfuradas, o perfil triangular invertido gera uma fenda que se alarga para jusante. Os sólidos passam livremente sem encavalar, enquanto as partículas retidas formam uma camada fácil de remover por raspadores ou retrolavagem.",
          "Fabricados em duas orientações de fenda: cilindros de fenda axial com arames paralelos ao eixo longitudinal para raspagem mecânica linear, e cilindros de fenda circunferencial com arames helicoidais contínuos para tambores rotativos e trommels.",
        ],
        keyPoints: [
          "Geometria contínua antiobstrução de arame em V mantém alta área aberta com baixa perda de carga operacional.",
          "Perfis e barras de suporte soldados por resistência evitam colapso sob picos de pressão e raspagem mecânica.",
          "Construção 100% em aço inoxidável suporta limpeza ultrassônica, retrolavagem e lavagens químicas repetidas.",
        ],
        image: {
          src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-product-photograph-of-sever-9a20f1.png",
          alt: "Montagem cilíndrica de tela em cunha soldada de precisão",
        },
      },
      specTable: {
        heading: "Parâmetros Padrão de Cilindros Filtrantes",
        intro: "Fabricação sob medida conforme desenho com flanges, anéis de extremidade e conexões roscadas integradas.",
        columns: [
          { key: "parameter", label: "Parâmetro", align: "left" },
          { key: "standardRange", label: "Faixa Padrão", align: "left" },
          { key: "customRange", label: "Opções Especiais", align: "left" },
          { key: "notes", label: "Observações", align: "left" },
        ],
        rows: [
          {
            parameter: "Abertura da fenda",
            standardRange: "0.02 – 5.0 mm",
            customRange: "Tolerâncias estritas",
            notes: "Espaçamento uniforme ao longo de toda a circunferência e comprimento",
          },
          {
            parameter: "Diâmetro externo",
            standardRange: "10 – 1200 mm",
            customRange: "Ferramentais dedicados",
            notes: "De pequenos elementos de tubulação a tambores trommel de grande porte",
          },
          {
            parameter: "Sentido do fluxo",
            standardRange: "FOTI / FITO",
            customRange: "Face lisa invertida",
            notes: "Fluxo de fora para dentro ou de dentro para fora",
          },
          {
            parameter: "Ligas padrão",
            standardRange: "AISI 304, AISI 316, 316L",
            customRange: "Análises sob pedido",
            notes: "Selecionadas para resistência à corrosão química e desgaste operacional",
          },
          {
            parameter: "Terminações",
            standardRange: "Bordas lisas, anéis soldados",
            customRange: "Flanges usinadas, roscas",
            notes: "Integração precisa em carcaças de filtros e equipamentos existentes",
          },
        ],
        caption: "Valores baseados nos limites de fabricação do arame em cunha soldado por resistência.",
      },
      features: {
        heading: "Capacidades de Engenharia",
        intro: "Cada cilindro é fabricado para separação sólido-líquido eficiente e durabilidade em regime contínuo.",
        items: [
          {
            title: "Geometria em V de Precisão",
            text: "Bordas lisas que reduzem turbulências e evitam entupimento das fendas.",
          },
          {
            title: "Estrutura Rígida Reforçada",
            text: "Barras de sustentação robustas que previnem colapso sob pressão diferencial elevada.",
          },
          {
            title: "Direções de Fluxo Reversíveis",
            text: "Disponível nas opções FOTI e FITO com superfície de trabalho perfeitamente lisa.",
          },
          {
            title: "Higiênico e Regenerável",
            text: "Soldagem sem frestas adequada para sanitização CIP e lavagens químicas agressivas.",
          },
        ],
      },
      related: {
        heading: "Explore as Variantes de Fenda",
        intro: "Selecione a orientação geométrica mais compatível com o sistema de limpeza do seu equipamento.",
        cards: [
          {
            title: "Cilindros de Fenda Axial",
            text: "Fendas contínuas paralelas ao eixo do cilindro oferecem guias lineares limpas para sistemas de raspadores mecânicos e filtração radial.",
            to: "/AxialSlotCylindersPage",
            badge: "Paralela ao eixo",
            image: {
              src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-product-photograph-of-an-ax-45d1d0.webp",
              alt: "Cilindro de arame em cunha com fenda axial e perfis longitudinais",
            },
            linkLabel: "Ver Cilindros Axiais",
          },
          {
            title: "Cilindros de Fenda Circunferencial",
            text: "Arames de superfície enrolados continuamente na circunferência sobre barras axiais de suporte, projetados para trommels rotativos e cilindros de alimentação interna.",
            to: "/CircumferentialSlotCylindersPage",
            badge: "Circunferencial",
            image: {
              src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-product-photograph-of-a-cir-c1a7f2.webp",
              alt: "Cilindro de arame em cunha com ranhura circunferencial para trommel rotativo",
            },
            linkLabel: "Ver Cilindros Circunferenciais",
          },
        ],
      },
      cta: {
        id: "cylindrical-screens-quote",
        eyebrow: "Soluções Cilíndricas Customizadas",
        heading: "Solicite um Orçamento para Cilindros Filtrantes",
        lead: "Envie suas dimensões, abertura de fenda e parâmetros operacionais para uma proposta técnica completa.",
        secondaryLabel: "Voltar para Telas em Cunha",
        secondaryTo: "/WedgeWireScreenPage",
        note: "Relatórios de ensaio de materiais e identificação positiva disponíveis sob pedido no pedido.",
      },
    },
  },
};
